export const GENERIC_VALIDATION_ERROR = "Dati Errati e/o Incompleti";

export function OverlayFieldLabel({ children, required = false }) {
  return (
    <span className="overlay_validation_label">
      <span>{children}</span>
      {required ? <span className="overlay_required_mark" aria-hidden="true">*</span> : null}
    </span>
  );
}

export function OverlayFieldError({ message = "" }) {
  if (!message) return null;
  return <span className="overlay_field_validation" role="alert">{message}</span>;
}

export function OverlayFieldInfo({ children, show = true }) {
  if (!show) return null;
  return <span className="overlay_field_validation overlay_field_info" role="note">{children}</span>;
}

export function overlayFieldClass(error, className = "") {
  return ["overlay_field", className, error ? "is_invalid" : ""].filter(Boolean).join(" ");
}

export function RequiredMark({ show = true }) {
  return show ? <span className="overlay_required_mark" aria-hidden="true">*</span> : null;
}

export function validationFieldProps(error, className = "overlay_field") {
  const message = String(error || "").trim();
  return {
    className: `${className}${message ? " overlay_validation_invalid" : ""}`,
    ...(message ? { "data-validation-error": message, "aria-invalid": "true" } : {}),
  };
}

function fieldLabelText(field) {
  const label = field?.querySelector(":scope > span, :scope > .overlay_validation_label");
  return String(label?.textContent || "").replace(/\*/g, "").trim();
}

function isRequiredOverlayField(field) {
  if (!field || field.classList.contains("overlay_check_field")) return false;
  if (field.querySelector(".overlay_required_mark")) return true;
  if (field.querySelector("input[required], textarea[required], select[required]")) return true;
  const directLabel = field.querySelector(":scope > span");
  return /\*/.test(String(directLabel?.textContent || ""));
}

function fieldControl(field) {
  return field?.querySelector(
    'input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]), textarea, select, .vesta_select_trigger, .overlay_select_button'
  ) || null;
}

function controlValue(control) {
  if (!control) return "";
  if (control.matches(".vesta_select_trigger")) return String(control.dataset.vestaValue || "").trim();
  if (control.matches(".overlay_select_button")) return String(control.dataset.value || control.getAttribute("data-selected-value") || "").trim();
  return String(control.value ?? "").trim();
}

function nativeValidationMessage(control) {
  if (!(control instanceof HTMLInputElement) && !(control instanceof HTMLTextAreaElement) && !(control instanceof HTMLSelectElement)) return "";
  if (control.disabled || control.readOnly) return "";
  if (typeof control.checkValidity !== "function" || control.checkValidity()) return "";
  if (control.validity?.typeMismatch) return "Valore non valido";
  if (control.validity?.rangeUnderflow || control.validity?.rangeOverflow) return "Valore fuori intervallo";
  if (control.validity?.stepMismatch || control.validity?.badInput) return "Valore non valido";
  if (control.validity?.patternMismatch) return "Formato non valido";
  if (control.validity?.tooLong || control.validity?.tooShort) return "Lunghezza non valida";
  return "Valore non valido";
}

export function clearOverlayFieldValidation(field) {
  if (!field) return;
  field.classList.remove("overlay_validation_invalid");
  field.removeAttribute("data-validation-error");
  if (!field.classList.contains("is_invalid")) field.removeAttribute("aria-invalid");
}

export function validateOverlayField(field) {
  if (!field || field.hidden || field.closest('[aria-hidden="true"]')) return "";
  const control = fieldControl(field);
  if (!control || control.disabled) return "";
  const label = fieldLabelText(field) || "Campo";
  if (isRequiredOverlayField(field) && !controlValue(control)) return `${label} obbligatorio`;
  return nativeValidationMessage(control);
}

export function validateOverlayCard(card) {
  if (!card) return true;
  let firstInvalid = null;
  card.querySelectorAll(".overlay_field").forEach((field) => {
    if (field.querySelector(":scope > .overlay_field_validation:not(.overlay_field_info)")) return;
    const message = validateOverlayField(field);
    if (!message) {
      clearOverlayFieldValidation(field);
      return;
    }
    field.classList.add("overlay_validation_invalid");
    field.setAttribute("data-validation-error", message);
    field.setAttribute("aria-invalid", "true");
    if (!firstInvalid) firstInvalid = field;
  });
  if (firstInvalid) {
    firstInvalid.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
    return false;
  }
  return true;
}

export function validateActiveOverlay() {
  const overlays = [...document.querySelectorAll(".overlay_backdrop")];
  const activeOverlay = overlays[overlays.length - 1];
  return validateOverlayCard(activeOverlay?.querySelector(".overlay_card"));
}

export function focusOverlayValidationField(field) {
  if (!field) return;
  field.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
  const control = field.querySelector('input:not([type="hidden"]):not(:disabled), textarea:not(:disabled), button:not(:disabled), [tabindex]:not([tabindex="-1"])');
  if (control && typeof control.focus === "function") {
    window.setTimeout(() => control.focus({ preventScroll: true }), 180);
  }
}

export function focusFirstValidationError() {
  window.requestAnimationFrame(() => {
    const overlays = [...document.querySelectorAll(".overlay_backdrop")];
    const activeOverlay = overlays[overlays.length - 1];
    const field = activeOverlay?.querySelector(".overlay_validation_invalid, .overlay_field.is_invalid");
    focusOverlayValidationField(field);
  });
}
